/**
 * UPT-2079 L12b (spec web `SPEC-UPT-2079-L12.md` §4, « Démonstration ») — le contrôle de livraison du DÉPÔT DE DÉMONSTRATION : fonction pure, sans réseau, qui rend un
 * EXEMPLE de `DeliveryCheck` pour une version du dépôt local. Ce n'est PAS le générateur : le serveur reste la seule référence (« le serveur juge, le web montre », décision 3),
 * et ce que l'écran affiche d'un serveur ne vient jamais d'ici.
 *
 *   - Un objet dont le code est déjà pris par la plateforme (`auditevent`, `todo`… : les codes du socle de `moduleSpecLocalCoreFirst`) donne l'obstacle `entity.taken` sur
 *     `/entities/<code>` (`detail` = code du module, comme le serveur) : c'est le cas de la version 1 de `dz-cmdb` (`auditevent`). Tout autre document est `ok: true`.
 *   - La réparation locale (`moduleSpecLocalRepair(…, { coreFirst: true })`) retire ou renomme ces objets : la version réparée est donc verte, comme à la recette.
 *   - Les nombres d'objets, de cycles de vie (états, transitions) sont LUS dans le document ; le nombre de fichiers est un chiffre d'exemple (le plan de référence de
 *     `pret-materiel` : 26 fichiers pour 2 objets, soit 10 fichiers de module et 8 par objet), jamais un calcul du générateur.
 * Ne lance jamais.
 */
import type { DeliveryCheck, GeneratorIssue } from '../data/contracts';
import type { ModuleSpecDocument } from '../data/moduleSpecContracts';
import { PLATFORM_ENTITY_CODES } from './moduleSpecLocalCoreFirst';

/** Fichiers d'exemple par module et par objet (plan de référence de `pret-materiel` : 26 fichiers, 2 objets). */
const DEMO_FILES_PER_MODULE = 10;
const DEMO_FILES_PER_ENTITY = 8;

type Json = Record<string, unknown>;
const asRecord = (value: unknown): Json | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Json) : null;
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

export interface LocalDeliveryInput {
  version: number;
  spec: ModuleSpecDocument;
  /** Nombre d'avertissements et de remarques du rapport de la version. */
  warnings: number;
  notes: number;
  /** Horodatage ISO du contrôle. */
  checkedAt: string;
}

export function moduleSpecLocalDeliveryCheck(input: LocalDeliveryInput): DeliveryCheck {
  const document = asRecord(input.spec);
  const moduleRecord = asRecord(document?.module);
  const moduleCode = asString(moduleRecord?.code);
  const entities = asRecord(document?.entities) ?? {};
  const codes = Object.keys(entities);

  const errors: GeneratorIssue[] = codes
    .filter((code) => PLATFORM_ENTITY_CODES.has(code))
    .map((code) => ({ severity: 'error', code: 'entity.taken', path: `/entities/${code}`, detail: moduleCode }));

  const workflows = codes.flatMap((code) => {
    const workflow = asRecord(asRecord(entities[code])?.workflow);
    const states = Array.isArray(workflow?.states) ? workflow.states.length : 0;
    const transitions = Array.isArray(workflow?.transitions) ? workflow.transitions.length : 0;
    return states > 0 ? [{ entity: code, states, transitions }] : [];
  });

  return {
    version: input.version,
    ok: errors.length === 0,
    mode: 'new',
    checkedAt: input.checkedAt,
    errors,
    warnings: input.warnings,
    notes: input.notes,
    entities: codes.length,
    artifacts: DEMO_FILES_PER_MODULE + DEMO_FILES_PER_ENTITY * codes.length,
    workflows,
    notGenerated: [],
  };
}
